import { Typography, Paper, Toolbar, Button } from '@material-ui/core';
import * as React from 'react';
import { Location } from 'interfaces';
import {
  DelayedLinearProgress,
  ConfirmDialog,
  AddLocationDialog,
  SnackbarContext,
  ResponsiveIconButton,
  SortableTable,
  TableControls,
  RouterLink,
} from 'components';
import AddIcon from '@material-ui/icons/Add';
import { LocationService } from 'services/LocationService';
import { SortableTableHeader } from 'components/SortableTable/components';

const columns: SortableTableHeader[] = [
  { key: 'name', label: 'Location' },
  { key: 'controls', sortable: false },
];

export const LocationsView: React.FC = () => {
  const snackbar = React.useContext(SnackbarContext);
  const [loading, setLoading] = React.useState(true);
  const [locations, setLocations] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [openEdit, setOpenEdit] = React.useState(false);
  const [editLocation, setEditLocation] = React.useState<Location>();
  const [deleteLocation, setDeleteLocation] = React.useState<Location>();
  const [openConfirm, setOpenConfirm] = React.useState(false);

  const loadLocations = React.useCallback(
    async function loadLocations() {
      try {
        const response = await LocationService.getLocations();
        setLocations(response);
        setLoading(false);
      } catch (error) {
        snackbar.error(error);
      }
    },
    [snackbar],
  );

  React.useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  const rows = locations.map((location: Location) => ({
    key: location.id.toString(),
    cells: [
      {
        key: 'name',
        display: (
          <Button
            component={RouterLink}
            to={`/location/${location.id.toString()}/job-types`}
            variant="text"
            size="small"
            color="secondary"
          >
            {location.name}
          </Button>
        ),
        sortValue: location.name,
      },
      {
        key: 'controls',
        display: (
          <TableControls
            onEdit={() => {
              setEditLocation(location);
              setOpenEdit(true);
            }}
            onDelete={() => {
              setOpenConfirm(true);
              setDeleteLocation(location);
            }}
          />
        ),
      },
    ],
  }));

  return (
    <React.Fragment>
      <DelayedLinearProgress loading={loading} />
      <Paper>
        <Toolbar style={{ justifyContent: 'space-between' }}>
          <Typography variant="h6">Locations Table</Typography>
          <ResponsiveIconButton
            onClick={() => setOpen(true)}
            color="primary"
            icon={AddIcon}
          >
            Add Location
          </ResponsiveIconButton>
        </Toolbar>
        <SortableTable
          columns={columns}
          rows={rows}
          // loading={queryLoading}
          emptyTableText="No locations available yet."
          emptyTableCreateAction={() => setOpen(true)}
          disablePagination
        />
      </Paper>
      <AddLocationDialog
        loading={loading}
        dialogProps={{
          open: open,
          onClose: () => setOpen(false),
          fullWidth: true,
        }}
        refresh={() => loadLocations()}
        locationList={locations}
      />
      <AddLocationDialog
        loading={loading}
        dialogProps={{
          open: openEdit,
          onClose: () => setOpenEdit(false),
          fullWidth: true,
        }}
        location={editLocation}
        refresh={() => loadLocations()}
        locationList={locations}
      />
      <ConfirmDialog
        title="Are you sure you want to delete this location?"
        dialogProps={{
          open: openConfirm,
          onClose: () => {
            setOpenConfirm(false);
            setDeleteLocation(undefined);
          },
        }}
        onConfirm={() => {
          if (!deleteLocation) {
            return;
          }
          LocationService.deleteLocation(deleteLocation.id);
          loadLocations();
        }}
      />
    </React.Fragment>
  );
};
